import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, DataTable, FilterChips, Pagination, Pill, type Column } from "@atlas/ui/web";
import { contagens, POR_PAGINA_PADRAO, recortar } from "../../lib/recorte";
import { atlas } from "../../lib/sdk";
import { useUnidadeAtiva } from "../../lib/unidade-ativa";
import { PageHeader, inp } from "./_ui";

export function PrefeituraAnuencia() {
  // A organizacao sobre a qual esta tela opera — a do login, ou a atendida
  // escolhida no seletor do cabecalho. Fora do shell (teste isolado) devolve
  // vazio, e a tela se comporta como antes da feature.
  const { chaveDaUnidade, opcoesDoSdk } = useUnidadeAtiva();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["prefeitura", "anuencia", ...chaveDaUnidade()],
    queryFn: () => atlas.prefeitura.anuencia(opcoesDoSdk()),
  });
  const [aceitoPor, setAceitoPor] = useState("");
  const [check, setCheck] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  // O filtro util aqui e a VERSAO do termo — as linhas sao
  // {id, versao, aceitoPor, aceitoEm, ip}, nao ha situacao nem banco. Quando o
  // termo muda de versao, a pergunta que se faz e "quem aceitou a versao nova?".
  const [versaoFiltro, setVersaoFiltro] = useState("");
  useEffect(() => { setPagina(1); }, [versaoFiltro]);

  const historico = q.data?.historico ?? [];
  const versoes = Array.from(new Set(historico.map((a) => a.versao))).sort().reverse();
  const contagemVersao = contagens(historico, (a) => a.versao);
  const filtradas = versaoFiltro ? historico.filter((a) => a.versao === versaoFiltro) : historico;
  const daPagina = recortar(filtradas, pagina, porPagina);

  const aceitar = useMutation({
    mutationFn: () => atlas.prefeitura.aceitarAnuencia(aceitoPor, opcoesDoSdk()),
    onSuccess: () => { setCheck(false); setAceitoPor(""); qc.invalidateQueries({ queryKey: ["prefeitura"] }); },
  });

  const vigente = q.data?.vigente ?? null;

  const columns: Column<{ id: string; versao: string; aceitoPor: string; aceitoEm: string; ip?: string }>[] = [
    { key: "id", header: "ID", mono: true },
    { key: "versao", header: "Versão" },
    { key: "aceitoPor", header: "Aceito por" },
    { key: "aceitoEm", header: "Data", render: (a) => new Date(a.aceitoEm).toLocaleString("pt-BR") },
    { key: "ip", header: "IP", render: (a) => a.ip ?? "—" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <PageHeader title="Anuência de dados" subtitle="Autorização formal (opt-in auditável) para uso da base de servidores pela averbadora e consignatárias." />

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", color: "var(--text-dim)", textTransform: "uppercase" }}>Termo {q.data?.versaoAtual}</span>
          {vigente ? <Pill variant="averbado">Vigente — aceito por {vigente.aceitoPor}</Pill> : <Pill variant="pendente">Pendente de aceite</Pill>}
        </div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 12, lineHeight: 1.6 }}>{q.data?.termo}</p>

        <div style={{ marginTop: 12, display: "grid", gap: 10, maxWidth: 480 }}>
          {vigente ? <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Já há uma anuência vigente. Você pode registrar uma nova confirmação (fica no histórico).</span> : null}
          <input style={inp} placeholder="Nome do responsável (RH/Gestor)" value={aceitoPor} onChange={(e) => setAceitoPor(e.target.value)} />
          <label style={{ display: "flex", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={check} onChange={(e) => setCheck(e.target.checked)} />
            Li e autorizo o uso da base conforme o termo acima (LGPD).
          </label>
          <div><Button onClick={() => aceitar.mutate()} disabled={!check || aceitoPor.length < 2 || aceitar.isPending}>{aceitar.isPending ? "Registrando…" : vigente ? "Registrar nova confirmação" : "Registrar anuência"}</Button></div>
          {aceitar.isError ? <p style={{ color: "var(--danger-500)", fontSize: 13 }}>{(aceitar.error as Error).message}</p> : null}
        </div>
      </Card>

      <Card>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", color: "var(--text-dim)", textTransform: "uppercase" }}>Histórico auditável</span>
        <div style={{ marginTop: 10 }}>
          {versoes.length > 1 ? (
            <FilterChips
              label="Versão"
              value={versaoFiltro}
              onChange={setVersaoFiltro}
              options={[
                { value: "", label: "Todas", count: historico.length },
                ...versoes.map((v) => ({ value: v, label: v, count: contagemVersao[v] ?? 0 })),
              ]}
            />
          ) : null}
          <DataTable
            columns={columns}
            rows={daPagina.itens}
            rowKey={(a) => a.id}
            loading={q.isLoading}
            emptyState={versaoFiltro
              ? "Ninguém aceitou esta versão do termo."
              : "Nenhuma anuência registrada."}
          />
          <Pagination
            total={daPagina.total}
            pagina={daPagina.pagina}
            porPagina={porPagina}
            onPagina={setPagina}
            onPorPagina={setPorPagina}
            rotulo="anuências"
          />
        </div>
      </Card>
    </div>
  );
}
